import React from 'react';
import './AppShowcase.css';

/**
 * Seção "Todas as suas finanças em um só app" — novo design (Atualizacoes do site/code.html).
 * Estilos isolados em AppShowcase.css (prefixo as-) para não colidir com o resto do sistema.
 */

const BARS = [
  { label: 'Abr', h: 'h-8', color: 'bg-emerald-300' },
  { label: 'Mai', h: 'h-10', color: 'bg-emerald-300' },
  { label: 'Jun', h: 'h-9', color: 'bg-emerald-400' },
  { label: 'Jul', h: 'h-11', color: 'bg-emerald-400' },
  { label: 'Ago', h: 'h-12', color: 'bg-emerald-500' },
  { label: 'Set', h: 'h-14', color: 'bg-[#00E676] shadow-[0_0_10px_rgba(0,230,118,0.7)]', current: true },
];

const FEATURES = [
  {
    title: 'Resposta Rápida',
    text: 'Lançamento em segundos e sincronização imediata do seu fluxo.',
    icon: (
      <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
      </svg>
    ),
  },
  {
    title: 'Gestão na Palma da Mão',
    text: 'Controle total das suas finanças direto no celular, onde estiver.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
        <rect height="20" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" width="10" x="7" y="2" />
        <path d="M11 18h2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Visão 100% Clara',
    text: 'Gráficos intuitivos para entender cada gasto sem complicação.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
        <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Facilidade de Acesso',
    text: 'Navegação fluida e interface limpa pensada para o seu dia a dia.',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

function Widget({ className = '', animation, iconBox, children }) {
  return (
    <div
      className={`hidden lg:flex items-center gap-3.5 absolute z-30 as-glass px-4 py-3 rounded-2xl shadow-[0_15px_35px_rgba(0,0,0,0.6)] ${animation} ${className}`}
    >
      {iconBox}
      <div>{children}</div>
    </div>
  );
}

export default function AppShowcase({ onStart, onDemo }) {
  return (
    <section id="app-completo" className="relative overflow-hidden bg-[#020604] text-slate-100 antialiased py-16 sm:py-20 lg:py-24 scroll-mt-20">
      {/* Luzes ambientes e grade de pontos */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="as-pulse-glow absolute -top-32 left-1/2 w-[1100px] h-[650px] bg-[radial-gradient(ellipse_at_center,_rgba(0,242,157,0.22)_0%,_rgba(5,213,136,0.12)_35%,_rgba(2,14,9,0)_72%)] blur-[100px]" />
        <div className="absolute top-[52%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[850px] h-[550px] bg-[radial-gradient(circle_at_center,_rgba(0,230,118,0.18)_0%,_rgba(16,185,129,0.08)_45%,_transparent_75%)] blur-[115px]" />
        <div className="as-pattern absolute inset-0 opacity-80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_40%,_#020604_95%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Título, subtítulo e botões */}
        <div className="text-center max-w-4xl mx-auto flex flex-col items-center pt-6">
          <h2 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.08] mb-5">
            Todas as suas finanças <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-emerald-300 via-[#00F29D] to-[#00E676] bg-clip-text text-transparent drop-shadow-[0_0_35px_rgba(0,242,157,0.35)]">
              em um só app.
            </span>
          </h2>

          <p className="text-base sm:text-lg lg:text-xl text-slate-300/90 font-normal leading-relaxed max-w-2xl mx-auto mb-9">
            Do lançamento rápido ao fluxo de caixa detalhado: tenha visão completa do seu dinheiro na palma da sua mão.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 w-full sm:w-auto mb-10 sm:mb-14">
            <button
              type="button"
              onClick={onStart}
              className="relative group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl font-bold text-base text-[#01160c] bg-gradient-to-r from-[#00F29D] via-[#00E676] to-[#05D588] shadow-[0_0_35px_rgba(0,242,157,0.5),0_0_12px_rgba(0,230,118,0.8)] hover:shadow-[0_0_55px_rgba(0,242,157,0.7),0_0_20px_rgba(0,230,118,1)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              <span>Começar agora gratuitamente</span>
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M5 12h14m-7-7l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              type="button"
              onClick={onDemo}
              className="relative group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-white font-semibold text-base as-pill hover:bg-emerald-500/20 hover:border-emerald-400/50 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-300 active:scale-[0.98] cursor-pointer"
            >
              <svg className="w-5 h-5 text-[#00F29D] transition-transform group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Ver demonstração interativa</span>
            </button>
          </div>
        </div>

        {/* Três celulares 3D + widgets flutuantes */}
        <div className="relative mt-2 sm:mt-4 max-w-6xl mx-auto w-full as-stage">
          <div className="as-floor absolute left-1/2 -bottom-10 -translate-x-1/2 w-[85%] h-28 pointer-events-none z-0" />

          {/* Widget 1: salário creditado */}
          <Widget
            animation="as-float-slow"
            className="-left-6 top-14"
            iconBox={
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-[#00E676] shadow-[0_0_12px_rgba(0,230,118,0.3)]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M7 11l5-5m0 0l5 5m-5-5v12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            }
          >
            <div className="text-[11px] font-medium text-slate-300">Salário creditado</div>
            <div className="as-mono text-sm font-bold text-white flex items-center gap-1.5 whitespace-nowrap">
              + R$ 6.200,00
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-400/20 text-[#00E676] border border-emerald-400/30">+14%</span>
            </div>
          </Widget>

          {/* Widget 2: economia do mês */}
          <Widget
            animation="as-float-delayed"
            className="-left-2 bottom-20"
            iconBox={
              <div className="w-10 h-10 rounded-xl bg-[#00F29D]/20 border border-[#00F29D]/40 flex items-center justify-center text-[#00F29D] shadow-[0_0_12px_rgba(0,242,157,0.3)]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            }
          >
            <div className="text-[11px] font-medium text-slate-300">Economia do mês</div>
            <div className="as-mono text-sm font-bold text-[#00E676] flex items-center gap-1.5 whitespace-nowrap">
              R$ 1.840 poupados
              <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
            </div>
          </Widget>

          {/* Widget 3: cartões adicionados */}
          <Widget
            animation="as-float-fast"
            className="-right-4 top-20"
            iconBox={
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400/30 to-emerald-700/30 border border-emerald-400/40 flex items-center justify-center relative shadow-[0_0_12px_rgba(0,242,157,0.25)]">
                <div className="w-4 h-3 rounded-sm border border-amber-300/80 bg-amber-400/40 flex items-center justify-center">
                  <span className="w-2 h-[1px] bg-amber-200" />
                </div>
              </div>
            }
          >
            <div className="text-[11px] font-medium text-slate-300">Cartões adicionados!</div>
            <div className="text-sm font-bold text-white flex items-center gap-1.5 whitespace-nowrap">
              Próxima Fatura disponível
              <span className="as-mono text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">ONLINE</span>
            </div>
          </Widget>

          {/* Widget 4: meta orçamentária */}
          <Widget
            animation="as-float-slow"
            className="-right-2 bottom-16"
            iconBox={
              <div className="as-mono relative w-9 h-9 rounded-full border-2 border-[#00F29D] border-r-transparent flex items-center justify-center text-[10px] font-extrabold text-[#00E676] shadow-[0_0_10px_rgba(0,242,157,0.3)]">
                88%
              </div>
            }
          >
            <div className="text-[11px] font-medium text-slate-300">Meta Orçamentária</div>
            <div className="text-xs font-semibold text-emerald-300 whitespace-nowrap">88% dentro do planejado</div>
          </Widget>

          <div className="flex items-center justify-center relative min-h-[570px] sm:min-h-[640px]">
            {/* Celular esquerdo: login */}
            <div className="hidden md:block absolute left-2 lg:left-12 w-[245px] lg:w-[275px] z-10 as-tilt-left">
              <div className="rounded-[46px] p-2.5 bg-gradient-to-b from-[#1c382b] via-[#091f16] to-[#040e0a] border border-emerald-400/40 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_25px_rgba(0,242,157,0.15)] relative">
                <div className="rounded-[38px] bg-[#05150f] p-5 pt-7 h-[495px] flex flex-col justify-between items-center text-center border border-white/10 relative overflow-hidden">
                  <div className="as-glare absolute inset-0 rounded-[38px]" />
                  <div className="w-20 h-4 bg-black/80 rounded-full mx-auto relative z-10 border border-white/5" />

                  <div className="flex flex-col items-center relative z-10 my-auto">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,242,157,0.45)] mb-4 border border-emerald-400/50 p-0.5 bg-gradient-to-tr from-[#00F29D]/40 to-emerald-950">
                      <img alt="Gastify" className="w-full h-full object-cover rounded-[14px]" src="/gastify-icon-192.png" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-white tracking-tight flex items-center gap-0.5">
                      Gastify<span className="text-[#00F29D]">.</span>
                    </h3>
                    <p className="text-xs text-emerald-300/80 mt-1 font-medium">Decisões conscientes</p>
                    <div className="as-mono mt-4 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] text-emerald-300">
                      v3.4 PRO EDITION
                    </div>
                  </div>

                  <div className="w-full space-y-2 relative z-10 mb-2">
                    <div className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#00F29D] to-[#05D588] text-[#02130a] text-xs font-bold shadow-[0_0_15px_rgba(0,242,157,0.3)]">
                      Entrar
                    </div>
                    <div className="w-full py-2.5 rounded-xl bg-emerald-950/70 border border-emerald-500/30 text-slate-300 text-xs font-semibold">
                      Criar conta gratuita
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Celular central: dashboard */}
            <div className="relative z-20 w-[305px] sm:w-[355px] as-center">
              <div className="rounded-[52px] p-3 bg-gradient-to-b from-[#25523d] via-[#103325] to-[#04120a] border-2 border-emerald-400/50 shadow-[0_30px_80px_rgba(0,0,0,0.95),0_0_50px_rgba(0,242,157,0.3)] relative">
                <div className="absolute -left-1 top-24 bottom-24 w-[2px] bg-gradient-to-b from-transparent via-[#00E676] to-transparent opacity-80 blur-[1px]" />
                <div className="absolute -right-1 top-24 bottom-24 w-[2px] bg-gradient-to-b from-transparent via-[#00F29D] to-transparent opacity-80 blur-[1px]" />

                <div className="rounded-[42px] bg-[#f8fafc] text-slate-800 p-4 pt-3 h-[590px] sm:h-[630px] flex flex-col justify-between overflow-hidden shadow-inner border border-slate-200/90 relative">
                  <div className="as-glare absolute inset-0 rounded-[42px]" />
                  <div>
                    {/* Status bar */}
                    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800 px-2 pt-1 pb-1 relative z-10">
                      <span>9:41</span>
                      <div className="w-24 h-5 bg-black rounded-full flex items-center justify-between px-2.5 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
                        <span className="as-mono text-[9px] text-white tracking-tighter">Live</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px]">
                        <span className="font-bold">5G</span>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3c-4.97 0-9 4.03-9 9 0 2.12.74 4.07 1.97 5.61L4.35 19.4c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l1.9-1.9C9.27 19.57 10.58 20 12 20c4.97 0 9-4.03 9-9s-4.03-9-9-9z" />
                        </svg>
                      </div>
                    </div>

                    {/* Saudação */}
                    <div className="flex items-center justify-between mt-2.5 px-1 relative z-10">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-600 to-[#00F29D] border border-emerald-300 flex items-center justify-center text-xs font-bold text-white shadow-sm">
                          JS
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-bold text-slate-800">Olá, Sofia</div>
                          <div className="text-[10px] text-slate-400 font-medium">Finanças sob controle total</div>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center border border-slate-200">
                        <svg className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>

                    {/* Métricas 2x2 */}
                    <div className="grid grid-cols-2 gap-2 mt-3 relative z-10">
                      <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm text-left">
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <div className="flex items-center gap-1 font-medium">
                            <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">↑</span>
                            <span>Receitas</span>
                          </div>
                          <span className="as-mono text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 rounded">+12%</span>
                        </div>
                        <div className="as-mono text-sm font-bold text-slate-800 mt-1">R$ 6.200</div>
                      </div>

                      <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm text-left">
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <div className="flex items-center gap-1 font-medium">
                            <span className="w-4 h-4 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center font-bold">↓</span>
                            <span>Despesas</span>
                          </div>
                          <span className="as-mono text-[9px] font-bold text-rose-500 bg-rose-50 px-1 rounded">-5%</span>
                        </div>
                        <div className="as-mono text-sm font-bold text-slate-800 mt-1">R$ 2.150</div>
                      </div>

                      <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm text-left">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="font-medium text-emerald-900">Saldo atual</span>
                          <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                          </svg>
                        </div>
                        <div className="as-mono text-sm font-extrabold text-emerald-700 mt-1">R$ 8.420</div>
                      </div>

                      <div className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-sm text-left">
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <span className="font-medium">Cartões</span>
                          <span className="text-emerald-500 font-bold">💳</span>
                        </div>
                        <div className="as-mono text-sm font-bold text-slate-800 mt-1">R$ 1.650</div>
                      </div>
                    </div>

                    {/* Gráfico de barras */}
                    <div className="mt-2.5 p-3 rounded-2xl bg-white border border-slate-100 shadow-sm text-left relative z-10">
                      <div className="flex items-center justify-between text-[11px] font-bold text-slate-700 mb-3">
                        <span>Últimos 6 meses</span>
                        <span className="as-mono text-[9px] text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                          ● R$ 2.340 média
                        </span>
                      </div>
                      <div className="flex items-end justify-between h-[76px] px-1">
                        {BARS.map((b) => (
                          <div key={b.label} className="flex flex-col items-center gap-1">
                            <div className={`w-6 rounded-t-md ${b.h} ${b.color}`} />
                            <span className={`text-[8px] ${b.current ? 'text-emerald-600 font-bold' : 'text-slate-400 font-medium'}`}>
                              {b.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Rosca de categorias */}
                    <div className="mt-2.5 p-2.5 rounded-2xl bg-white border border-slate-100 shadow-sm text-left relative z-10">
                      <span className="text-[10px] font-bold text-slate-700 block mb-1">Gastos por categoria</span>
                      <div className="flex items-center gap-3">
                        <div
                          className="relative w-14 h-14 rounded-full flex items-center justify-center shrink-0 shadow-inner"
                          style={{ background: 'conic-gradient(#10b981 0% 34%, #38bdf8 34% 52%, #f59e0b 52% 76%, #ec4899 76% 100%)' }}
                        >
                          <div className="as-mono w-8 h-8 rounded-full bg-white flex flex-col items-center justify-center text-[7px] font-bold text-slate-800 shadow-sm">
                            <span>R$ 2.15k</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[8px] text-slate-600 flex-1">
                          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Alimentação 34%</div>
                          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Moradia 24%</div>
                          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" />Transporte 18%</div>
                          <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-pink-500" />Lazer 14%</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Barra de navegação */}
                  <div className="pt-2 border-t border-slate-100 flex items-center justify-around text-slate-400 pb-1 relative z-10">
                    <div className="flex flex-col items-center text-emerald-600 text-[9px] font-bold">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                      <span>Início</span>
                    </div>
                    <div className="flex flex-col items-center text-[9px]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                      </svg>
                      <span>Lançar</span>
                    </div>
                    <div className="-mt-5">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-[#00F29D] text-[#02130a] flex items-center justify-center shadow-lg font-bold text-xl">
                        +
                      </div>
                    </div>
                    <div className="flex flex-col items-center text-[9px]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                      </svg>
                      <span>Cartões</span>
                    </div>
                    <div className="flex flex-col items-center text-[9px]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                      </svg>
                      <span>Mais</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Celular direito: cartões */}
            <div className="hidden md:block absolute right-2 lg:right-12 w-[245px] lg:w-[275px] z-10 as-tilt-right">
              <div className="rounded-[46px] p-2.5 bg-gradient-to-b from-[#1c382b] via-[#091f16] to-[#040e0a] border border-emerald-400/40 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_25px_rgba(0,242,157,0.15)] relative">
                <div className="rounded-[38px] bg-[#dbe4de] p-4 pt-6 h-[495px] flex flex-col justify-between text-left border border-white/20 relative overflow-hidden">
                  <div className="as-glare absolute inset-0 rounded-[38px]" />
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-extrabold text-slate-800">Meus Cartões</h4>
                      <span className="as-mono text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800">3 ativos</span>
                    </div>

                    <div className="p-3.5 rounded-2xl bg-gradient-to-tr from-[#050e0a] via-[#10241b] to-[#1f4a38] text-white shadow-lg border border-emerald-500/30 mb-3.5 relative overflow-hidden">
                      <div className="absolute -right-6 -bottom-6 w-20 h-20 bg-[#00F29D]/20 rounded-full blur-xl" />
                      <div className="flex justify-between items-start text-[10px] text-slate-300">
                        <span className="font-bold tracking-wider text-emerald-300">GASTIFY BLACK</span>
                        <span className="as-mono text-[9px] tracking-widest text-emerald-400 font-bold">VISA INFINITE</span>
                      </div>
                      <div className="mt-3 w-6 h-4 rounded-sm bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 border border-amber-300/60 shadow-sm flex items-center justify-center">
                        <div className="w-3.5 h-[1px] bg-amber-800/60" />
                      </div>
                      <div className="as-mono mt-3 text-[11px] tracking-widest text-slate-300">•••• 4589</div>
                      <div className="mt-2 flex justify-between items-center text-[10px]">
                        <span className="text-slate-400">R$ 1.650 fatura atual</span>
                        <span className="w-3.5 h-3.5 rounded-full bg-[#00E676]/80 shadow-[0_0_6px_#00E676]" />
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl bg-gradient-to-tr from-slate-900 via-slate-800 to-[#1e3328] text-white shadow-lg border border-slate-600/50 relative overflow-hidden">
                      <div className="flex justify-between items-start text-[10px] text-slate-300">
                        <span className="font-bold tracking-wider text-slate-200">PLATINUM</span>
                        <span className="as-mono text-[9px] tracking-widest text-[#00F29D] font-bold">MASTERCARD</span>
                      </div>
                      <div className="mt-3 w-6 h-4 rounded-sm bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 border border-amber-300/60 shadow-sm" />
                      <div className="as-mono mt-3 text-[11px] tracking-widest text-slate-300">•••• 9821</div>
                      <div className="mt-2 flex justify-between items-center text-[10px]">
                        <span className="text-slate-400">R$ 420 fatura atual</span>
                        <span className="w-3.5 h-3.5 rounded-full bg-[#00F29D]/50" />
                      </div>
                    </div>
                  </div>

                  <div className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-800 to-emerald-700 text-white text-xs font-semibold text-center shadow-md relative z-10">
                    + Adicionar Novo Cartão
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Faixa de diferenciais */}
        <div className="mt-14 sm:mt-[4.5rem] border-t border-emerald-500/15 pt-8 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto text-left">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="p-5 rounded-2xl as-glass transition-transform duration-300 hover:scale-[1.02] flex flex-col justify-between"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center text-[#00E676] mb-3">
                  {f.icon}
                </div>
                <div>
                  <h4 className="text-base font-bold text-white tracking-tight">{f.title}</h4>
                  <p className="text-xs text-slate-300/80 mt-1.5 leading-relaxed font-normal">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
